import { CollectingDataMessage } from 'components/loaders';

import { CostReportPageContainer } from 'features/cost-report/components/CostReportPageContainer/CostReportPageContainer';
import { useCostOverTime } from 'features/cost-report/hooks/useCostOverTime';
import { CostOverTimeSectionProvider } from 'features/cost-report/providers/CostOverTimeSectionProvider';
import { CostReportProvider } from 'features/cost-report/providers/CostReportProvider';
import { CostReportTab } from 'features/cost-report/types/common';
import { useParams } from 'hooks/useParams';
import { WorkloadCostOverTimeProvider } from './_providers';

import { DailyComputeSpendDetails } from './DailyComputeSpendDetails';
import { PodMetricsProvider, PodMetricsSection } from './PodMetricsSection';
import { NormalizedCostSection } from '../../../components/NormalizedCostSection';
import {
  ComputeSpendProvider,
  ComputeSpendSection,
} from '../../../compute-spend';
import { DailyMetricsSection } from '../../../DailyMetricsSection';
import { useGetWorkloadDetailsEnabledTabs } from '../../_hooks/useGetWorkloadDetailsEnabledTabs';
import { WorkloadDetailsRouteProps } from '../../_types';
import { WorkloadDetailsBreadcrumbs } from '../../components/WorkloadDetailsBreadcrumbs';
import { WorkloadDetailsTitleWithActions } from '../../components/WorkloadDetailsTitleWithActions';
import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { Typography, Box, Stack } from '@cast/design-system';

const Page = () => {
  const detailsTabs = useGetWorkloadDetailsEnabledTabs();
  const { workloadName, namespace, workloadType } =
    useParams<WorkloadDetailsRouteProps>();

  const { isCollecting, hasData, noDataReason, error, refetch } =
    useCostOverTime();

  if (error) {
    return (
      <Stack gap={12}>
        <WorkloadDetailsBreadcrumbs workloadName={workloadName} />
        <Stack gap={8}>
          <Typography variant="h3">{workloadName}</Typography>
        </Stack>
        <Box mt="63px">
          <FailedToLoad title="Failed to load your cost report" />
        </Box>
      </Stack>
    );
  }

  return (
    <CostReportPageContainer
      currentTab={CostReportTab.COMPUTE_COST}
      {...detailsTabs}
      title={
        <WorkloadDetailsTitleWithActions
          workloadName={workloadName}
          namespace={namespace}
          workloadType={workloadType}
        />
      }
      hasError={!!error}
      showNoData={!hasData && !isCollecting}
      noDataReason={noDataReason}
      errorMessage={`Failed to load ${workloadName.toLowerCase()} cost report`}
      refetch={refetch}
      loadingMessage={
        isCollecting && (
          <Box display="flex" alignItems="center">
            <CollectingDataMessage
              title="Collecting data"
              description="Data about namespaces is being collected. The report will be populated as soon as possible."
            />
          </Box>
        )
      }
    >
      {!isCollecting && <DailyMetricsSection />}

      <ComputeSpendProvider>
        <ComputeSpendSection />
      </ComputeSpendProvider>

      <CostOverTimeSectionProvider>
        <NormalizedCostSection costType="requested" />
        <PodMetricsProvider>
          <PodMetricsSection />
        </PodMetricsProvider>
      </CostOverTimeSectionProvider>

      <DailyComputeSpendDetails />
    </CostReportPageContainer>
  );
};

export const ComputeCostDetails = () => {
  const { cluster } = useCluster();
  const params = useParams<WorkloadDetailsRouteProps>();

  return (
    <CostReportProvider>
      <WorkloadCostOverTimeProvider
        clusterId={cluster.id}
        workloadName={params.workloadName}
        workloadType={params.workloadType}
        namespace={params.namespace}
      >
        <Page />
      </WorkloadCostOverTimeProvider>
    </CostReportProvider>
  );
};
